/** Who is signed in in the browser, independent of how (cpg.computer sign-in, or the local dev bypass). */
import { createContext, useContext } from 'react'

export interface Session {
  isLoading: boolean
  isAuthenticated: boolean
  /** The access token the server checks. */
  getToken: () => Promise<string | undefined>
  /** Sends the person to sign in, and back to `returnTo` afterwards. */
  login: (returnTo?: string) => void
  logout: () => void
  error?: string
  /**
   * One silent token refresh, after the server answered 401 mid-session. True when a fresh token is ready.
   * Calls made while one is running share it.
   */
  refresh: () => Promise<boolean>
  /** True once a refresh has failed: the Gate shows "Your session ended" until the person signs in again. */
  ended: boolean
  end: () => void
}

export const SessionContext = createContext<Session | null>(null)

export function useSession(): Session {
  const s = useContext(SessionContext)
  if (!s) throw new Error('useSession outside a session provider')
  return s
}

export const RETURN_KEY = 'app.returnTo'

/**
 * Wraps an async check so calls made while one is running share its result, and a check that has failed once is
 * never run again (no refresh loops after the session has ended).
 */
export function sharedAttempt(run: () => Promise<boolean>): () => Promise<boolean> {
  let running: Promise<boolean> | null = null
  let failed = false
  return () => {
    if (failed) return Promise.resolve(false)
    running ??= run()
      .catch(() => false)
      .then((ok) => {
        running = null
        if (!ok) failed = true
        return ok
      })
    return running
  }
}
